import { useMapsLibrary } from '@vis.gl/react-google-maps'
import Processing from 'ext-ui/components/Processing/Processing'
import React, { useEffect, useRef, useState } from 'react'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { useTranslate } from 'src/localization/Trns'

import styles from './AddressAutocomplete.style'
import { MileagePlace } from './MileageMap'

interface AddressAutocompleteProps {
  fieldLabel: string
  placeholder: string
  value?: MileagePlace
  defaultText?: string
  onPlaceSelect: (place: MileagePlace | undefined) => void
  onInputChange?: () => void
  testId?: string
}

const DEBOUNCE_MS = 250
const MAX_SUGGESTIONS = 5
const ADDRESS_TYPES = ['street_address', 'route', 'subpremise']

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  fieldLabel,
  placeholder,
  value,
  defaultText,
  onPlaceSelect,
  onInputChange,
  testId,
}) => {
  const placesLib = useMapsLibrary('places')
  const translate = useTranslate()
  const [query, setQuery] = useState<string>(value?.name ?? defaultText ?? '')
  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompleteSuggestion[]
  >([])
  const [open, setOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const listRef = useRef<HTMLUListElement | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null)

  const getSessionToken = (): google.maps.places.AutocompleteSessionToken => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current =
        new google.maps.places.AutocompleteSessionToken()
    }
    return sessionTokenRef.current
  }

  useEffect(() => {
    setQuery(value?.name ?? defaultText ?? '')
  }, [value?.name, defaultText])

  useEffect(() => {
    if (!placesLib || !query || query.length < 2) {
      setSuggestions([])
      setLoading(false)
      return
    }
    if (value && value.name === query) {
      setSuggestions([])
      setLoading(false)
      return
    }

    setLoading(true)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        includedPrimaryTypes: ADDRESS_TYPES,
        includedRegionCodes: ['us'],
        sessionToken: getSessionToken(),
      })
        .then(({ suggestions: results }) => {
          const placeSuggestions = results.filter(
            (s) => s.placePrediction !== null,
          )
          setSuggestions(placeSuggestions.slice(0, MAX_SUGGESTIONS))
          setActiveIndex(-1)
          setLoading(false)
        })
        .catch(() => {
          setSuggestions([])
          setLoading(false)
        })
    }, DEBOUNCE_MS)

    return (): void => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [placesLib, query, value])

  const handleSelect = async (
    suggestion: google.maps.places.AutocompleteSuggestion,
  ): Promise<void> => {
    const prediction = suggestion.placePrediction
    if (!placesLib || !prediction) return

    try {
      const place = prediction.toPlace()
      await place.fetchFields({
        fields: ['location', 'formattedAddress', 'displayName'],
      })
      sessionTokenRef.current = null

      if (!place.location) return

      onPlaceSelect({
        placeId: prediction.placeId,
        name:
          place.formattedAddress ?? place.displayName ?? prediction.text.text,
        lat: place.location.lat(),
        lng: place.location.lng(),
      })
      setSuggestions([])
      setOpen(false)
    } catch {
      sessionTokenRef.current = null
    }
  }

  const handleChange = (next: string): void => {
    setQuery(next)
    setOpen(true)
    onInputChange?.()
    if (value && next !== value.name) {
      onPlaceSelect(undefined)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (!showSuggestions) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1))
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault()
      void handleSelect(suggestions[activeIndex])
    } else if (e.key === 'Escape') {
      setOpen(false)
      setActiveIndex(-1)
    }
  }

  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[role="option"]')
      ;(items[activeIndex] as HTMLElement)?.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex])

  const showSuggestions = open && suggestions.length > 0
  const showLoading = open && loading && suggestions.length === 0

  return (
    <div css={styles} className="AddressAutocomplete" data-testid={testId}>
      <TextInputField
        design={TextInputFieldDesigns.New}
        fieldLabel={fieldLabel}
        placeholder={placeholder}
        value={query}
        onChange={handleChange}
        onFocus={(): void => setOpen(true)}
        onBlur={(): void => {
          setTimeout(() => {
            setOpen(false)
            setActiveIndex(-1)
          }, 150)
        }}
        onKeyDown={handleKeyDown}
        testId={testId}
      />
      {showLoading && (
        <div className="suggestions-loading">
          <Processing aria-hidden="true" />
          <span>{translate('Loading...')}</span>
        </div>
      )}
      {showSuggestions && (
        <ul
          className="suggestions"
          role="listbox"
          id="address-suggestions"
          ref={listRef}
        >
          {suggestions.map((s, i) => {
            const prediction = s.placePrediction
            if (!prediction) return null
            return (
              <li
                key={prediction.placeId}
                id={`suggestion-${i}`}
                className={`suggestion${i === activeIndex ? ' active' : ''}`}
                role="option"
                aria-selected={i === activeIndex}
                onMouseDown={(e): void => e.preventDefault()}
                onClick={(): void => void handleSelect(s)}
                onKeyDown={(e): void => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    void handleSelect(s)
                  }
                }}
                onMouseEnter={(): void => setActiveIndex(i)}
              >
                {prediction.text.text}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default AddressAutocomplete
